import {Drawer} from 'components/customer/Product/entity/Drawer';
import {DrawerRunner} from 'components/customer/Product/entity/DrawerRunner';
import {DrawerSystem} from 'shared/types/DrawerSystem';

export const getFaceHeight = (
    drawers: Drawer[],
    runnerIndex: number,
    totalDrawerHeight: number,
    cabinetDrawerBottom = 0,
    cabinetDrawerTop = 0,
    cabinetDrawerGap = 0
): number => {
    let offset = 0;
    let count = drawers.length;

    drawers.forEach((drawer, index) => {
        if (
            (drawer.drawer_face_height > -1 && drawer.drawer_style == 0) ||
            (runnerIndex == drawers.length - 1 && index < runnerIndex)
        ) {
            offset += parseFloat(String(drawer.drawer_face_height));
            count -= 1;
        }
    });

    let height =
        (totalDrawerHeight -
            offset -
            (cabinetDrawerBottom > 0 ? cabinetDrawerBottom : 0) -
            (cabinetDrawerTop > 0 ? cabinetDrawerTop : 0) -
            cabinetDrawerGap * (drawers.length - 1)) /
        count;

    if (cabinetDrawerTop < 0 && runnerIndex == 0) {
        height -= cabinetDrawerTop;
    }

    if (cabinetDrawerBottom < 0 && runnerIndex == drawers.length - 1) {
        height -= cabinetDrawerBottom;
    }

    if (Number(height) === height && height % 1 !== 0) {
        return parseFloat(height.toFixed(1));
    }

    return height;
};

export const readjustLastAutoHeight = (
    drawers: Drawer[],
    totalDrawerHeight: number,
    cabinetDrawerBottom = 0,
    cabinetDrawerTop = 0,
    cabinetDrawerGap = 0
) => {
    const drawersFiltered = drawers
        .map((drawer, index) => ({...drawer, index}))
        .filter((drawer) => drawer.drawer_style == 1);

    if (drawersFiltered.length) {
        const lastIndex = drawersFiltered.pop().index;

        const totalFaceHeightExceptlast = drawers
            .filter((drawer, index) => index != lastIndex)
            .reduce(
                (partialSum, drawer) => partialSum + drawer.drawer_face_height,
                0
            );

        let height =
            totalDrawerHeight -
            (cabinetDrawerBottom +
                cabinetDrawerTop +
                cabinetDrawerGap * (drawers.length - 1)) -
            totalFaceHeightExceptlast;

        if (Number(height) === height && height % 1 !== 0) {
            height = parseFloat(height.toFixed(1));
        }

        drawers[lastIndex].drawer_face_height = height;
    }

    return drawers;
};

// below this the drawer layout stacks; shared so the presets turn horizontal at
// exactly the same width rather than drifting apart
export const BELOW_DESKTOP = '@media (max-width: 991.98px)';

// a negative margin is an overhang, so only a positive one takes space away
export const faceSpace = (
    total: number,
    top: number,
    bottom: number,
    gap: number,
    count: number
): number =>
    total -
    (top > 0 ? top : 0) -
    (bottom > 0 ? bottom : 0) -
    gap * Math.max(0, count - 1);

export const MIXED = 'Mixed';

// What every drawer holds, or MIXED when they disagree
export const sharedValue = (drawers: Drawer[], fieldName: string): string => {
    const values = drawers
        .map((drawer) => String(drawer?.[String(fieldName)] ?? '').trim())
        .filter((value) => value !== '' && value !== '-1');

    if (values.length === 0) return '';

    return values.every((value) => value === values[0]) ? values[0] : MIXED;
};

// What every drawer holds while they agree, otherwise the last cabinet-level
// choice. Empty means no default to follow or override.
export const cabinetDefault = (
    drawers: Drawer[],
    fieldName: string,
    chosen: string
): string => {
    const shared = sharedValue(drawers, fieldName);

    return shared !== '' && shared !== MIXED ? shared : chosen;
};

// The shortest face a runner fits
export const minimumFaceHeight = (
    drawer: Drawer,
    drawerSystems: DrawerSystem[],
    typeFieldName: string,
    runnerHeight?: number
): number => {
    const height = runnerHeight ?? toNumber(drawer?.drawer_runner_height);

    if (!(height > 0)) return 0;

    const system = (drawerSystems ?? []).find(
        (option) => option.id === toNumber(drawer?.[String(typeFieldName)])
    );

    return (
        height +
        toNumber(system?.topClearance) +
        toNumber(system?.bottomClearance)
    );
};

export interface ShortestRunner {
    type: number;
    height: number;
}

// The lowest a face can go whatever runner it holds now: the shortest runner
// its type offers. Unknown for a type whose runners were never fetched.
export const faceFloor = (
    drawer: Drawer,
    drawerSystems: DrawerSystem[],
    typeFieldName: string,
    shortest: ShortestRunner
): number =>
    shortest && shortest.type === toNumber(drawer?.[String(typeFieldName)])
        ? minimumFaceHeight(
              drawer,
              drawerSystems,
              typeFieldName,
              shortest.height
          )
        : 0;

export interface FaceRange {
    min: number;
    max: number;
}

// Down to the face's own floor, and up to what leaves the other autos theirs
// once they share the rest. Shares round to 0.1 and the last auto takes what
// is left, which can fall short; holding back 0.05 × n × (n - 1) covers it.
export const faceRange = (
    drawers: Drawer[],
    index: number,
    faceHeightFieldName: string,
    styleFieldName: string,
    floors: number[]
): FaceRange => {
    const heightAt = (at: number) =>
        toNumber(drawers[Number(at)]?.[String(faceHeightFieldName)]);
    const autos = drawers
        .map((drawer, at) => at)
        .filter(
            (at) =>
                at !== index &&
                toNumber(drawers[Number(at)]?.[String(styleFieldName)]) === 1
        );
    const min = Math.ceil(floors[Number(index)] ?? 0);

    // nothing on auto to give way, so the face can only shrink; the layout
    // always keeps one on auto, so only saved data reaches here
    if (!autos.length) return {min, max: heightAt(index)};

    const spare = autos.reduce((total, at) => total + heightAt(at), 0);
    const floor = Math.max(0, ...autos.map((at) => floors[Number(at)] ?? 0));
    const rounding = 0.05 * autos.length * (autos.length - 1);

    return {
        min,
        max: Math.floor(
            heightAt(index) + spare - autos.length * floor - rounding + 1e-6
        ),
    };
};

// Whether a drawer carries the cabinet's hardware. An unknown default, or a
// drawer left without a runner, is nothing to judge against.
export const followsCabinet = (
    drawer: Drawer,
    cabinet: {type: string; runner: string},
    typeFieldName: string,
    runnerFieldName: string
): boolean => {
    if (!drawer) return false;

    const matches = (fieldName: string, value: string) => {
        const held = String(drawer?.[String(fieldName)] ?? '').trim();

        return value === '' || held === '' || held === '-1' || held === value;
    };

    return (
        matches(typeFieldName, cabinet.type) &&
        matches(runnerFieldName, cabinet.runner)
    );
};

// a runner always reads "150x500 : 150mm Cream Side."; anything without the
// separator is not one, such as the -1 parked there when nothing fits
export const shortRunner = (runner: unknown): string => {
    const text = String(runner ?? '').trim();

    return text.includes(' :') ? text.split(' :')[0] : '';
};

export const toNumber = (value: unknown): number => {
    return typeof value === 'string'
        ? parseFloat(value)
        : typeof value === 'number'
          ? value
          : 0;
};

export const isDrillOnly = (item: DrawerRunner | string | number): boolean => {
    // Assume objects passed are instances of this class
    if (typeof item === 'object' && 'drillOnly' in item) {
        return item.drillOnly;
    }

    // Numbers passed are always invalid.
    if (typeof item == 'number' || item == '-1') {
        return false;
    }

    // Strings will always end with the string defined above
    return /\(drill only\)$/.test(item);
};

const drillOnlySuffixPattern = / \(drill only\)$/;

export const runnerIdsMatch = (
    optionId: string | number | undefined,
    value: string | number | undefined
): boolean =>
    String(optionId).replace(drillOnlySuffixPattern, '') ===
    String(value).replace(drillOnlySuffixPattern, '');

export const hasInnerUnitCost = (
    drawerSystem: Pick<DrawerSystem, 'drawerRunnerType' | 'maxInnerUnitCost'>,
    isInnerDrawer: boolean,
    isDrawerComponent: boolean
): boolean =>
    !(isInnerDrawer || isDrawerComponent) ||
    drawerSystem.drawerRunnerType === 'None' ||
    drawerSystem.maxInnerUnitCost > 0;

// Drawn proportionally, a short cabinet loses the room to hold a height field,
// so this is linear across the heights cabinets have and flat outside them.
const STAGE_MIN_MM = 350;
const STAGE_MAX_MM = 1000;
const STAGE_MIN = 280;
const STAGE_MAX = 520;
// folded, the same drawing would cost most of the screen
const COMPACT_MIN = 240;
const COMPACT_MAX = 360;
// recalculated on every keystroke, so the card steps rather than shivers
const STAGE_STEP = 20;

export const stageHeight = (total: number, compact = false): number => {
    const min = compact ? COMPACT_MIN : STAGE_MIN;
    const max = compact ? COMPACT_MAX : STAGE_MAX;
    const held = Math.min(STAGE_MAX_MM, Math.max(STAGE_MIN_MM, total));
    const along = (held - STAGE_MIN_MM) / (STAGE_MAX_MM - STAGE_MIN_MM);

    return Math.round((min + along * (max - min)) / STAGE_STEP) * STAGE_STEP;
};
